// Timezone helpers for meeting/start-time handling.
//
// my_time stores each start time as a wall-clock HH:MM plus an IANA zone tag.
// The device's current zone is auto-detected; the user can override it. Saved
// Quick Adds (templates) carry the zone their time is anchored to, and the
// backend converts that anchored time into the user's current zone on apply.

export interface ZoneOption {
  id: string; // IANA zone, e.g. "America/New_York"
  label: string; // human label, e.g. "Eastern"
}

// Curated list of common (mostly US) zones the user travels between.
export const COMMON_ZONES: ZoneOption[] = [
  { id: 'America/New_York', label: 'Eastern' },
  { id: 'America/Chicago', label: 'Central' },
  { id: 'America/Denver', label: 'Mountain' },
  { id: 'America/Phoenix', label: 'Arizona (no DST)' },
  { id: 'America/Los_Angeles', label: 'Pacific' },
  { id: 'America/Anchorage', label: 'Alaska' },
  { id: 'Pacific/Honolulu', label: 'Hawaii' },
];

/** The device's IANA zone, e.g. "America/Denver". Falls back to UTC. */
export function getDeviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Short zone abbreviation for a zone on a given date, e.g. "EDT" / "MST".
 * The date matters because DST changes the abbreviation. `dateStr` is a
 * YYYY-MM-DD string; when omitted, today is used.
 */
export function zoneAbbrev(zone?: string | null, dateStr?: string | null): string {
  if (!zone) return '';
  try {
    // Anchor at noon UTC on the given day so the target zone's DST state is
    // resolved correctly regardless of the device's own zone.
    const d = new Date(`${dateStr || new Date().toISOString().slice(0, 10)}T12:00:00Z`);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'short',
    }).formatToParts(d);
    return parts.find((p) => p.type === 'timeZoneName')?.value || zone;
  } catch {
    return zone;
  }
}

/** A short human label for a zone, e.g. "Eastern (EDT)". */
export function zoneLabel(zone?: string | null, dateStr?: string | null): string {
  if (!zone) return '';
  const known = COMMON_ZONES.find((z) => z.id === zone);
  const abbr = zoneAbbrev(zone, dateStr);
  if (known) return abbr ? `${known.label} (${abbr})` : known.label;
  // Unknown zone: show the city part of the IANA id + abbreviation.
  const city = zone.split('/').pop()?.replace(/_/g, ' ') || zone;
  return abbr ? `${city} (${abbr})` : city;
}

/**
 * Format a start time with its zone for display, e.g. "10:30 EDT".
 * Returns '' when there is no time. Omits the zone tag for legacy entries
 * that have no zone recorded.
 */
export function formatStartWithZone(
  time?: string | null,
  zone?: string | null,
  dateStr?: string | null,
): string {
  if (!time) return '';
  const abbr = zone ? zoneAbbrev(zone, dateStr) : '';
  return abbr ? `${time} ${abbr}` : time;
}
